import React, { useEffect, useState, useRef, useCallback } from 'react';
import { SearchBox, SearchLayerCloseReason } from './../../components/inputs/SearchBox';
import { SearchResultItem, ISearchResultClickEvent } from './SearchResultItem';
import './PortalSearchBox.less';
import classNames from 'classnames';
import { ActionHeader } from '@decisions/server/DecisionsFramework/ServiceLayer/Actions/';
import { isDefaultGridAction } from 'utils/ActionUtils';
import { getActions, searchItems } from './PortalSearchBoxHelper';
import { useTrackFocus } from 'components/inputs/hooks/useTrackFocus';
import { ActionsCallout } from 'components/actions/ActionsCallout';
export interface IPortalSearchBoxProps {
  pageSize: number;
  isCollapsed: boolean;
  runBaseAction: (entityId: string, action: ActionHeader) => void;
  showContextMenu: (event: ISearchResultClickEvent & { actions: ActionHeader[] }) => void;
  hideContextMenu: () => void;
  searchContextActions?: ActionHeader[];
}

const getClassName = (isCollapsed: boolean, isExpanded: boolean) =>
  classNames('dpc-portal-searchbox', {
    'dpc-portal-searchbox--collapsed': isCollapsed,
    'dpc-portal-searchbox--expanded': isExpanded,
  });

export function PortalSearchBox({
  pageSize,
  runBaseAction,
  showContextMenu,
  hideContextMenu,
  isCollapsed,
  searchContextActions,
}: IPortalSearchBoxProps) {
  const [isExpanded, setExpanding] = useState(false);
  const [isLayerVisible, setIsLayerVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const { isFocused, trackFocus, trackBlur } = useTrackFocus();
  const actionMenuSourceRef: React.MutableRefObject<HTMLElement | null> = useRef(null);

  const closeLayer = useCallback(
    (closeReason: SearchLayerCloseReason | null, event?: Event) => {
      if (
        closeReason === SearchLayerCloseReason.clickOutside &&
        (event?.target as HTMLElement)?.closest('.dp-actions-callout') != null
      ) {
        return;
      }

      if (closeReason !== SearchLayerCloseReason.inputBlur && closeReason !== SearchLayerCloseReason.itemChosen) {
        setIsLayerVisible(false);
        setSearchText('');
        hideContextMenu();
      }
    },
    [hideContextMenu]
  );
  const openLayer = useCallback(() => {
    setIsLayerVisible(true);
  }, []);

  const root = useRef<HTMLLIElement | null>(null);
  const input = useRef<HTMLInputElement | null>(null);
  const searchBox = useRef<HTMLDivElement | null>(null);

  const expandSearchBox = useCallback(() => {
    setExpanding(true);
  }, []);

  useEffect(() => {
    if (root.current) {
      //TODO alex: ideally need to use "forwardRef" for SearchBox. But how to get both ref for SearchBox and Ref for SearchLayer?
      // How to pass ref through several components correctly? PortalSearchBox => SearchBox => SearchField
      // Beside that SearchBox is generic component and using "forwardRef" breaks typings.
      input.current = root.current.querySelector('#portal-searchbox .dpc-search-field__input') as HTMLInputElement;
      searchBox.current = root.current.querySelector('#portal-searchbox') as HTMLDivElement;
    }
  }, []);

  useEffect(() => {
    setExpanding(false);
  }, [isCollapsed]);

  useEffect(() => {
    if (isExpanded && input.current != null) {
      input.current.focus();

      let listener = (event: Event) => {
        const layer = document.getElementById('portal-searchbox-layer');

        const target = event.target as Node;
        if (
          searchBox.current?.contains(target) === true ||
          layer?.contains(target) === true ||
          (event.target as HTMLElement).closest('.dp-actions-callout') != null
        ) {
          return;
        }

        setExpanding(false);
      };

      //NOTE alex: I use "document.body" instead of using just "document" intentionally.
      // When using "document.addEventListener" it will prevent calling function inside "useOnClickOutside" hook
      // and search layer will not be removed on click outside.
      document.body.addEventListener('mousedown', listener);
      document.body.addEventListener('touchstart', listener);

      return () => {
        document.body.removeEventListener('mousedown', listener);
        document.body.removeEventListener('touchstart', listener);
      };
    }
  }, [isExpanded]);

  const onResultItemClick = useCallback(
    async (event: ISearchResultClickEvent) => {
      hideContextMenu();

      const actions = await getActions(event.entity);
      const defaultAction = actions.find(action => isDefaultGridAction(action));
      if (defaultAction) {
        closeLayer(null);
        setExpanding(false);
        runBaseAction(event.entity.Id, defaultAction);
      } else {
        actionMenuSourceRef.current = event.target;
        showContextMenu({ ...event, ...{ actions } });
      }
    },
    [closeLayer, hideContextMenu, runBaseAction, showContextMenu]
  );

  const onContextMenu = useCallback(
    async (event: ISearchResultClickEvent) => {
      hideContextMenu();

      actionMenuSourceRef.current = event.target;
      const actions = await getActions(event.entity);
      showContextMenu({ ...event, ...{ actions } });
    },
    [hideContextMenu, showContextMenu]
  );

  const handleBlur = (e: React.FocusEvent) => {
    closeLayer(SearchLayerCloseReason.inputBlur);
    trackBlur(e);
  };

  return (
    <li className="dp-portal-header__menu-item dp-portal-header__menu-item--growing" ref={root}>
      {!isExpanded && (
        <button
          className={classNames('dp-portal-header__icon', 'dp-portal-header__icon-search', {
            'dp-portal-header__icon-search--visible': isCollapsed,
          })}
          aria-label="show searchbar"
          onClick={expandSearchBox}
        ></button>
      )}
      <SearchBox
        id={'portal-searchbox'}
        className={getClassName(isCollapsed, isExpanded)}
        submitOnEnter={true}
        //TODO: Translate
        placeholder={isFocused ? 'Press Enter to Search' : ''}
        loadBtnClass={'dp-portal-searchbox-layer__load-more'}
        noResult={<div className={'dp-portal-searchbox-layer__no-data-found'}>No Result Found</div>}
        layerControlProps={{
          isOpen: isLayerVisible,
          onClose: closeLayer,
          onOpen: openLayer,
        }}
        inputProps={{
          searchText,
          setSearchText,
        }}
        layerProps={{
          id: 'portal-searchbox-layer',
          width: isExpanded ? undefined : 600,
          placement: 'bottom-end',
          className: 'dp-portal-searchbox-layer',
          offset: '0, 2',
        }}
        search={{
          pageSize,
          searchFn: searchItems,
        }}
        itemTemplate={({ item, isFocused, searchText }) => (
          <SearchResultItem
            isCurrent={isFocused}
            entity={item}
            key={item.Id}
            searchText={searchText}
            onClick={onResultItemClick}
            onContextMenu={onContextMenu}
          />
        )}
        onItemChosen={(element, item) => {
          onResultItemClick({
            entity: element,
            target: item,
          });
        }}
        doNotEmitOnBlur={true}
        onBlur={handleBlur}
        onFocus={trackFocus}
      />
      {searchContextActions && searchContextActions.length && (
        <ActionsCallout
          placement="bottom"
          nestedPlacement="right"
          preLoadedActions={searchContextActions}
          onClose={hideContextMenu}
          onAction={() => {
            hideContextMenu();
            closeLayer(null);
          }}
          sourceRef={actionMenuSourceRef}
        />
      )}
    </li>
  );
}
